import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { IAttachment, IAttachmentTypes, IInsertAttachment, IInsertAttachmentResponse } from '../models/attachment-types.model';
import { ValidateAndDefault } from '../models/validate-and-default.model';
import { IInsertUpdateData } from '../models/insert-update-data.model';
import { FormItemField, FormItemGroup } from '../models/form-item.model';
import { IModalFormMetadataModel } from '../models/modal-form-metadata.model';
import { OfficeFieldsValuesObj } from '../models/office-data.model';
import { FormEventsModalConfig } from '../SingleGridForm/events/models/form-events-modal-config.model';

@Injectable({
  providedIn: 'root',
})
export class OfficeService {
  private http = inject(HttpClient);

  public getAttachmentList(objectParams: { co: number; form: string; keyID: number; UniqueAttchID: string }): Observable<IAttachment[]> {
    const params = new HttpParams({ fromObject: objectParams });
    return this.http.get<IAttachment[]>('/Office/GetAttachmentList', { params });
  }

  public insertAttachment(attachmentData: IInsertAttachment): Observable<IInsertAttachmentResponse> {
    return this.http.post<IInsertAttachmentResponse>('/Office/InsertAttachment', attachmentData);
  }

  public updateAttachmentMetadata(
    co: number,
    form: string,
    keyID: number,
    description: string,
    attachmentID: number,
    attachmentTypeID: number
  ): Observable<never> {
    return this.http.post<never>('/Office/UpdateAttachmentMetadata', {
      co: co,
      form: form,
      keyID: keyID,
      attachmentID: attachmentID,
      description: description,
      attachmentTypeID: attachmentTypeID,
    });
  }

  public deleteAttachment(co: number, form: string, keyID: number, attachmentID: number): Observable<never> {
    return this.http.post<never>('/Office/DeleteAttachment', {
      co: co,
      form: form,
      keyID: keyID,
      attachmentID: attachmentID,
    });
  }

  public getAttachmentTypes(co: number): Observable<IAttachmentTypes[]> {
    const params = new HttpParams().append('co', co);
    return this.http.get<IAttachmentTypes[]>('/Office/AttachmentTypes', { params });
  }

  getCompanies() {
    return this.http.get('/Office/GetCompanies').toPromise();
  }

  getForms(company: any, module: any) {
    return this.http.get('/Office/GetForms?co=' + company + '&module=' + module).toPromise();
  }

  getFormMetadata(tableName: any, company: any) {
    return this.http.get('/Office/GetFormMetadata?form=' + tableName + '&co=' + company).toPromise();
  }

  getFormData(
    tableName: any,
    company: any,
    orderby: any,
    keyid: any,
    rowsPerPage: any,
    pageNumber: any,
    where: any,
    parentForm: string,
    parentFormData: string,
    includeMetadata: boolean
  ) {
    return this.http
      .post('/Office/GetData', {
        form: tableName,
        co: company,
        rowsPerPage: rowsPerPage,
        pageNumber: pageNumber,
        orderby: orderby,
        keyid: keyid,
        where: where,
        parentForm: parentForm,
        parentFormData: parentFormData,
        includeMetadata: includeMetadata,
      })
      .toPromise();
  }
  /**
   * @param {string} currentCompany
   * @param {string} currentForm
   * @param {string} postData stringify object
   * @param {number} keyID
   * @param {string[]} dirtyFields
   * @returns {IInsertUpdateData} - Observable
   */
  public insertUpdateData(co: string, form: string, formData: string, keyID: number, dirtyFields: string[]): Observable<IInsertUpdateData> {
    return this.http.post<IInsertUpdateData>('/Office/InsertUpdateData', {
      co: co,
      form: form,
      formData: formData,
      keyID: keyID,
      dirtyFields: dirtyFields,
    });
  }
  /**
   * @param {FormEventsModalConfig} paramsObj
   * @returns {IModalFormMetadataModel} - Observable
   */
  public getModalMetadata(paramsObj: FormEventsModalConfig): Observable<IModalFormMetadataModel> {
    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get<IModalFormMetadataModel>('/Office/ModalForm', { params });
  }
  /**
   * @param {FormEventsModalConfig} paramsObj
   * @param {OfficeFieldsValuesObj} modalFormData
   * @returns {string} - Observable
   */
  public postModalForm(paramsObj: FormEventsModalConfig, modalFormData: OfficeFieldsValuesObj): Observable<string> {
    return this.http.post<string>('/Office/ModalForm', {
      ...paramsObj,
      modalFormData,
    });
  }

  deleteRow(co: any, form: any, keyID: any) {
    return this.http
      .post('/Office/DeleteData', {
        co: co,
        form: form,
        keyID: keyID,
      })
      .toPromise();
  }

  getAttachmentData(co: any, form: any, keyID: any, attachmentID: any) {
    return this.http
      .get('/Office/GetAttachmentData?co=' + co + '&form=' + form + '&keyID=' + keyID + '&attachmentID=' + attachmentID)
      .toPromise();
  }
  /**
   * @param {string} currentForm
   * @returns {FormItemGroup[]} - Observable
   */
  public getFormItemGroups(form: string): Observable<FormItemGroup[]> {
    const params = new HttpParams().set('form', form);
    return this.http.get<FormItemGroup[]>('/Office/GetFormItemGroups', { params });
  }
  /**
   * @param {string} currentForm
   * @returns {FormItemField[]} - Observable
   */
  public getFormItemFields(form: string): Observable<FormItemField[]> {
    const params = new HttpParams().set('form', form);
    return this.http.get<FormItemField[]>('/Office/GetFormItemFields', { params });
  }

  getFormReports(co: any, form: any) {
    return this.http.get('/Office/getFormReports?co=' + co + '&form=' + form).toPromise();
  }

  getLookup(co: any, form: any, seq: any, lookup: any, formData: any, searchString: any) {
    return this.http
      .post('/Office/getLookup', {
        co: co,
        form: form,
        seq: seq,
        lookup: lookup,
        formData: formData,
        searchString: searchString,
      })
      .toPromise();
  }

  getStaticLookups(co: any, form: any) {
    return this.http.get('/Office/getStaticLookups?co=' + co + '&form=' + form).toPromise();
  }

  getVaInquiries(co) {
    return this.http.get('/Office/GetVaInquiries?co=' + co).toPromise();
  }

  getVaInquiryData(co, queryName, paramValues) {
    const url = '/Office/GetVaInquiryData?co=' + co + '&queryName=' + queryName;
    let params = '';
    for (let i = 0; i < paramValues.length; i++) {
      params += '&paramValues[' + i + ']=' + encodeURIComponent(paramValues[i]);
    }
    return this.http.get(url + params).toPromise();
  }

  getVaInquiryLookup(co, lookup, dependentData = null) {
    let url = '/Office/GetVaInquiryLookup?co=' + co + '&lookup=' + lookup;
    if (dependentData != null) url += '&dependentData=' + encodeURIComponent(dependentData);
    return this.http.get(url).toPromise();
  }

  getVaInquiryParameters(queryName) {
    return this.http.get('/Office/GetVaInquiryParameters?queryName=' + queryName).toPromise();
  }
  /**
   * @param {string} currentForm
   * @param {FormItemGroup[]} groups
   * @param {FormItemField[]} fields
   * @returns {never} - Observable
   */
  public updateFormState(form, groups, fields): Observable<never> {
    const url = '/Office/UpdateFormState';
    const postData = {
      form: form,
      groups: groups,
      fields: fields,
    };
    return this.http.post<never>(url, postData);
  }

  getExcelFile(tableName: any, company: any, orderby: any, keyid: any, where: any, columnInfo: any, fileName: any) {
    return this.http
      .post('/office/SaveExcelFile', {
        form: tableName,
        co: company,
        orderby: orderby,
        keyid: keyid,
        where: where,
        columns: columnInfo,
      })
      .toPromise()
      .then(
        response => {
          if (response['success'] == false) {
            return false;
          } else {
            const url = '/Office/GetExcelFile?formName=' + tableName + '&fileName=' + (fileName ?? tableName);
            window.location.href = url;
            return true;
          }
        },
        rejectResponse => {
          return false;
        }
      );
  }

  pendoTrack(formName) {
    if (formName == '1' || formName == null) return;

    const url = '/Office/PendoTrack';
    const postData = {
      formName: formName,
    };
    return this.http.post(url, postData).toPromise();
  }

  /**
   * @param {string} currentCompany
   * @param {string} currentForm
   * @param {number} seq - validating field seq
   * @param {string} formData - JSON stringify object values
   * @returns {ValidateAndDefault} - Observable
   */
  public validateAndDefault(co: string, form: string, seq: number, formData: string): Observable<ValidateAndDefault> {
    return this.http.post<ValidateAndDefault>('/Office/ValidateAndDefault', {
      co: co,
      form: form,
      seq: seq,
      formData: formData,
    });
  }
}
